import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import {
  DesignState,
  DrawingStroke,
  EffectItem,
  EffectType,
  IconOut,
  MAX_EFFECT_ITEMS,
  MAX_TEXT_ITEMS,
  PenType,
  RenderPreviewResponse,
  TemplateOut,
  TextItem,
  TextPosition,
  emptyDesign,
  emptyEffectItem,
  emptyTextItem,
  iconTextItem,
  newDrawingId,
} from "../types";

type LayerKind = "text" | "effect";

interface DesignContextValue {
  design: DesignState | null;
  template: TemplateOut | null;
  previewJson: Record<string, unknown> | null;
  isRendering: boolean;
  renderError: string | null;

  activeTextId: string | null;
  activeText: TextItem | null;
  activeEffectId: string | null;
  activeEffect: EffectItem | null;
  activeLayerKind: LayerKind;

  startDesign: (template: TemplateOut) => void;
  setActiveTextId: (id: string) => void;
  addText: () => void;
  addIconLayer: (icon: IconOut) => void;
  removeText: (id: string) => void;
  updateActiveText: (patch: Partial<TextItem>) => void;

  setActiveEffectId: (id: string) => void;
  addEffect: (effectType: EffectType, patch?: Partial<EffectItem>) => void;
  removeEffect: (id: string) => void;
  updateActiveEffect: (patch: Partial<EffectItem>) => void;

  // Generalized - operate on whichever layer (text or effect) is
  // currently active, so PositionControl/StickerCanvas/TimingControl
  // work identically for both without needing to know which kind they're
  // editing.
  updateActivePosition: (patch: Partial<TextPosition>) => void;
  activeTiming: { start_fraction: number; end_fraction: number } | null;
  updateActiveTiming: (patch: { start_fraction?: number; end_fraction?: number }) => void;
  updateLayerTiming: (kind: "text" | "effect", id: string, patch: { start_fraction?: number; end_fraction?: number }) => void;

  // Whichever picker modal (font/color/icon) is currently open registers
  // its own close function here. App.tsx's back navigation checks this
  // FIRST and, if set, closes just the modal instead of navigating the
  // screen - a single source of truth that doesn't depend on correctly
  // intercepting the hardware/Telegram back button at the DOM/SDK level,
  // which proved unreliable on some Android WebView builds.
  overlayClose: (() => void) | null;
  setOverlayClose: (fn: (() => void) | null) => void;

  // Freehand drawing tool (text-only stickers only) - activePen set
  // means "drawing mode is on"; StickerCanvas reads it to switch its
  // pointer handlers from move/rotate/scale into stroke capture.
  activePen: { penType: PenType; color: string } | null;
  setActivePen: (pen: { penType: PenType; color: string } | null) => void;
  addDrawingStroke: (points: [number, number][]) => void;
  addAiDrawingStrokes: (strokePoints: [number, number][][], color: string) => void;
  removeDrawingStroke: (id: string) => void;
  undoLastStroke: () => void;
  clearDrawings: () => void;

  // Eraser mode - mutually exclusive with activePen (drawing); on
  // while active, StickerCanvas hit-tests pointer position against
  // existing strokes and removes whichever one gets touched.
  isErasing: boolean;
  setIsErasing: (v: boolean) => void;

  // تراجع/إعادة (للجميع)
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  historyEpoch: number; // يزيد عند تراجع/إعادة/فتح مشروع (لتحديث خانات الإدخال)
  // آخر المشاريع (VIP): فتح مشروع محفوظ داخل المحرر
  resumeProject: (id: string, design: DesignState, template: TemplateOut) => void;
}

const HISTORY_MAX = 60;
const COALESCE_MS = 700; // سحب/كتابة متواصلة = خطوة وحدة

function newProjectId(): string {
  return "p" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}
function loadProjectId(): string | null {
  try {
    return window.localStorage.getItem("sh_project_id");
  } catch {
    return null;
  }
}
function persistProjectId(id: string) {
  try {
    window.localStorage.setItem("sh_project_id", id);
  } catch {
    /* التخزين غير متاح - مو مشكلة */
  }
}

const DesignContext = createContext<DesignContextValue | null>(null);

const PREVIEW_DEBOUNCE_MS = 350;

export function DesignProvider({ children }: { children: React.ReactNode }) {
  const [design, setDesign] = useState<DesignState | null>(null);
  const [template, setTemplate] = useState<TemplateOut | null>(null);
  const [previewJson, setPreviewJson] = useState<Record<string, unknown> | null>(null);
  const [isRendering, setIsRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);

  const [activeTextIdState, setActiveTextIdState] = useState<string | null>(null);
  const [activeEffectIdState, setActiveEffectIdState] = useState<string | null>(null);
  const [activeLayerKind, setActiveLayerKind] = useState<LayerKind>("text");
  const [overlayClose, setOverlayClose] = useState<(() => void) | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeq = useRef(0);

  // ---- تاريخ التعديلات (تراجع/إعادة) + حفظ المشاريع (VIP) ----
  const designRef = useRef<DesignState | null>(null);
  const pastRef = useRef<DesignState[]>([]);
  const futureRef = useRef<DesignState[]>([]);
  const lastPushRef = useRef(0);
  const [histTick, setHistTick] = useState(0);
  const [historyEpoch, setHistoryEpoch] = useState(0);
  const projectIdRef = useRef<string>(loadProjectId() ?? newProjectId());
  const isVipRef = useRef(false);
  const suppressSaveRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    persistProjectId(projectIdRef.current);
    api
      .get<{ membership: { tier: string } }>("/api/account")
      .then((r) => {
        isVipRef.current = r.membership.tier === "vip";
      })
      .catch(() => undefined);
  }, []);

  const queueProjectSave = useCallback((d: DesignState, lottieJson: Record<string, unknown>) => {
    if (!isVipRef.current) return;
    if (suppressSaveRef.current) {
      // أول معاينة بعد فتح مشروع قديم: ما نجدّد مدته بمجرد الفتح
      suppressSaveRef.current = false;
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      api.put(`/api/projects/${projectIdRef.current}`, { design: d, preview: lottieJson }).catch(() => undefined);
    }, 1800);
  }, []);

  // Resume an in-progress draft on mount (per-user, stored server-side) -
  // but NOT when the page was opened via a "?template=..." deep link
  // (from the bot's template-share button). Both this fetch and
  // App.tsx's deep-link handler run on mount independently; without this
  // guard, whichever one resolved LAST would silently overwrite the
  // other - so opening a specific template could randomly end up
  // showing an unrelated old draft from a previous session instead
  // (exactly what was happening: "old cached" content, or a refresh
  // landing on some unrelated template).
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("template")) {
      return;
    }
    api
      .get<DesignState | null>("/api/design")
      .then((draft) => {
        if (draft && draft.texts.length > 0) {
          const restored = { ...draft, effects: draft.effects ?? [], drawings: draft.drawings ?? [] };
          designRef.current = restored;
          setDesign(restored);
          setActiveTextIdState(draft.texts[0].id);
          setActiveLayerKind("text");
        }
      })
      .catch(() => {
        /* no draft yet, or offline - start fresh, not fatal */
      });
  }, []);

  const requestPreview = useCallback((current: DesignState) => {
    const hasContent = current.texts.some((t) => t.text.trim()) || current.effects.length > 0;
    if (!hasContent) {
      setPreviewJson(null);
      return;
    }
    const seq = ++requestSeq.current;
    setIsRendering(true);
    setRenderError(null);
    api
      .post<RenderPreviewResponse>("/api/render/preview", { design: current })
      .then((res) => {
        if (seq === requestSeq.current) {
          setPreviewJson(res.lottie_json);
          queueProjectSave(current, res.lottie_json);
        }
      })
      .catch((err) => {
        if (seq === requestSeq.current) {
          setRenderError(err instanceof Error ? err.message : "Render failed");
        }
      })
      .finally(() => {
        if (seq === requestSeq.current) setIsRendering(false);
      });
  }, [queueProjectSave]);

  const queuePreview = useCallback(
    (next: DesignState) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        requestPreview(next);
        api.put("/api/design", next).catch(() => undefined);
      }, PREVIEW_DEBOUNCE_MS);
    },
    [requestPreview],
  );

  const scheduleUpdate = useCallback(
    (next: DesignState) => {
      const prev = designRef.current;
      if (prev && prev !== next) {
        const now = Date.now();
        // نسجّل الحالة السابقة، إلا إذا كان نفس السحب/الكتابة المتواصلة
        if (pastRef.current.length === 0 || now - lastPushRef.current > COALESCE_MS) {
          pastRef.current.push(prev);
          if (pastRef.current.length > HISTORY_MAX) pastRef.current.shift();
        }
        lastPushRef.current = now;
        futureRef.current = [];
        setHistTick((n) => n + 1);
      }
      designRef.current = next;
      setDesign(next);
      queuePreview(next);
    },
    [queuePreview],
  );

  const resetHistory = useCallback(() => {
    pastRef.current = [];
    futureRef.current = [];
    lastPushRef.current = 0;
    designRef.current = null;
    setHistTick((n) => n + 1);
  }, []);

  const startDesign = useCallback(
    (tpl: TemplateOut) => {
      setTemplate(tpl);
      resetHistory();
      projectIdRef.current = newProjectId();
      persistProjectId(projectIdRef.current);
      const fresh = emptyDesign(tpl);
      setPreviewJson(null);
      setActiveTextIdState(fresh.texts[0].id);
      setActiveLayerKind("text");
      scheduleUpdate(fresh);
    },
    [scheduleUpdate, resetHistory],
  );

  // ---- تراجع / إعادة ----

  const applyHistory = useCallback(
    (target: DesignState) => {
      designRef.current = target;
      setHistoryEpoch((n) => n + 1);
      setDesign(target);
      // الطبقة النشطة قد تكون انحذفت أو رجعت: نصحّح المؤشرات
      const textOk = target.texts.some((x) => x.id === activeTextIdState);
      if (!textOk) setActiveTextIdState(target.texts[0]?.id ?? null);
      if (activeLayerKind === "effect" && !target.effects.some((e) => e.id === activeEffectIdState)) {
        setActiveLayerKind("text");
      }
      queuePreview(target);
    },
    [activeTextIdState, activeEffectIdState, activeLayerKind, queuePreview],
  );

  const undo = useCallback(() => {
    const prev = pastRef.current.pop();
    const cur = designRef.current;
    if (!prev || !cur) return;
    futureRef.current.push(cur);
    lastPushRef.current = 0;
    setHistTick((n) => n + 1);
    applyHistory(prev);
  }, [applyHistory]);

  const redo = useCallback(() => {
    const next = futureRef.current.pop();
    const cur = designRef.current;
    if (!next || !cur) return;
    pastRef.current.push(cur);
    lastPushRef.current = 0;
    setHistTick((n) => n + 1);
    applyHistory(next);
  }, [applyHistory]);

  // فتح مشروع محفوظ (آخر المشاريع - VIP)
  const resumeProject = useCallback(
    (id: string, d: DesignState, tpl: TemplateOut) => {
      resetHistory();
      projectIdRef.current = id;
      persistProjectId(id);
      suppressSaveRef.current = true;
      setHistoryEpoch((n) => n + 1);
      const full: DesignState = { ...d, effects: d.effects ?? [], drawings: d.drawings ?? [] };
      setTemplate(tpl);
      setPreviewJson(null);
      designRef.current = full;
      setDesign(full);
      setActiveTextIdState(full.texts[0]?.id ?? null);
      setActiveLayerKind("text");
      queuePreview(full);
    },
    [resetHistory, queuePreview],
  );

  // ---- text layers ----

  const setActiveTextId = useCallback((id: string) => {
    setActiveTextIdState(id);
    setActiveLayerKind("text");
  }, []);

  const addText = useCallback(() => {
    if (!design || design.texts.length >= MAX_TEXT_ITEMS) return;
    const item = emptyTextItem(template ?? undefined);
    setActiveTextIdState(item.id);
    setActiveLayerKind("text");
    scheduleUpdate({ ...design, texts: [...design.texts, item] });
  }, [design, template, scheduleUpdate]);

  const addIconLayer = useCallback(
    (icon: IconOut) => {
      if (!design || design.texts.length >= MAX_TEXT_ITEMS) return;
      const item = iconTextItem(icon, template ?? undefined);
      setActiveTextIdState(item.id);
      setActiveLayerKind("text");
      scheduleUpdate({ ...design, texts: [...design.texts, item] });
    },
    [design, template, scheduleUpdate],
  );

  const removeText = useCallback(
    (id: string) => {
      if (!design || design.texts.length <= 1) return; // always keep at least one
      const remaining = design.texts.filter((t) => t.id !== id);
      if (activeTextIdState === id) setActiveTextIdState(remaining[0].id);
      scheduleUpdate({ ...design, texts: remaining });
    },
    [design, activeTextIdState, scheduleUpdate],
  );

  const updateActiveText = useCallback(
    (patch: Partial<TextItem>) => {
      if (!design || !activeTextIdState) return;
      const texts = design.texts.map((t) => (t.id === activeTextIdState ? { ...t, ...patch } : t));
      scheduleUpdate({ ...design, texts });
    },
    [design, activeTextIdState, scheduleUpdate],
  );

  // ---- effect layers ----

  const setActiveEffectId = useCallback((id: string) => {
    setActiveEffectIdState(id);
    setActiveLayerKind("effect");
  }, []);

  const addEffect = useCallback(
    (effectType: EffectType, patch?: Partial<EffectItem>) => {
      if (!design || design.effects.length >= MAX_EFFECT_ITEMS) return;
      const item = { ...emptyEffectItem(effectType), ...(patch ?? {}) };
      setActiveEffectIdState(item.id);
      setActiveLayerKind("effect");
      scheduleUpdate({ ...design, effects: [...design.effects, item] });
    },
    [design, scheduleUpdate],
  );

  const removeEffect = useCallback(
    (id: string) => {
      if (!design) return;
      const remaining = design.effects.filter((e) => e.id !== id);
      if (activeEffectIdState === id) {
        // Fall back to the first text layer, since effects can go to zero.
        setActiveLayerKind("text");
        setActiveTextIdState(design.texts[0]?.id ?? null);
      }
      scheduleUpdate({ ...design, effects: remaining });
    },
    [design, activeEffectIdState, scheduleUpdate],
  );

  const updateActiveEffect = useCallback(
    (patch: Partial<EffectItem>) => {
      if (!design || !activeEffectIdState) return;
      const effects = design.effects.map((e) => (e.id === activeEffectIdState ? { ...e, ...patch } : e));
      scheduleUpdate({ ...design, effects });
    },
    [design, activeEffectIdState, scheduleUpdate],
  );

  // ---- freehand drawing tool (text-only stickers) ----

  const [activePen, setActivePen] = useState<{ penType: PenType; color: string } | null>(null);
  const [isErasing, setIsErasing] = useState(false);

  // Turning the eraser on while a pen is selected (or vice versa)
  // wouldn't make sense as simultaneous modes - StickerCanvas only
  // checks one flag at a time anyway, but keeping them mutually
  // exclusive here avoids a confusing "both active" state existing
  // even momentarily.
  const setActivePenExclusive = useCallback((pen: { penType: PenType; color: string } | null) => {
    if (pen) setIsErasing(false);
    setActivePen(pen);
  }, []);

  const setIsErasingExclusive = useCallback((v: boolean) => {
    if (v) setActivePen(null);
    setIsErasing(v);
  }, []);

  const addDrawingStroke = useCallback(
    (points: [number, number][]) => {
      if (!design || !activePen || points.length < 2) return;
      const stroke: DrawingStroke = {
        id: newDrawingId(),
        pen_type: activePen.penType,
        color: activePen.color,
        points,
      };
      scheduleUpdate({ ...design, drawings: [...design.drawings, stroke] });
    },
    [design, activePen, scheduleUpdate],
  );

  /** Commits several strokes at once (from the AI-drawing feature,
   * which returns a whole small drawing in one response) - separate
   * from addDrawingStroke since that one is tied to activePen/live
   * pointer capture, not a bulk one-shot commit. */
  const addAiDrawingStrokes = useCallback(
    (strokePoints: [number, number][][], color: string) => {
      if (!design || strokePoints.length === 0) return;
      const newStrokes: DrawingStroke[] = strokePoints
        .filter((pts) => pts.length >= 2)
        .map((points) => ({ id: newDrawingId(), pen_type: "regular", color, points }));
      if (newStrokes.length === 0) return;
      scheduleUpdate({ ...design, drawings: [...design.drawings, ...newStrokes] });
    },
    [design, scheduleUpdate],
  );

  const removeDrawingStroke = useCallback(
    (id: string) => {
      if (!design) return;
      const remaining = design.drawings.filter((d) => d.id !== id);
      if (remaining.length === design.drawings.length) return; // nothing matched, skip the update
      scheduleUpdate({ ...design, drawings: remaining });
    },
    [design, scheduleUpdate],
  );

  const undoLastStroke = useCallback(() => {
    if (!design || design.drawings.length === 0) return;
    scheduleUpdate({ ...design, drawings: design.drawings.slice(0, -1) });
  }, [design, scheduleUpdate]);

  const clearDrawings = useCallback(() => {
    if (!design || design.drawings.length === 0) return;
    scheduleUpdate({ ...design, drawings: [] });
  }, [design, scheduleUpdate]);

  // ---- generalized position/timing (whichever layer is active) ----

  const updateActivePosition = useCallback(
    (patch: Partial<TextPosition>) => {
      if (activeLayerKind === "effect") {
        if (!design || !activeEffectIdState) return;
        const effects = design.effects.map((e) =>
          e.id === activeEffectIdState ? { ...e, position: { ...e.position, ...patch } } : e,
        );
        scheduleUpdate({ ...design, effects });
      } else {
        if (!design || !activeTextIdState) return;
        const texts = design.texts.map((t) =>
          t.id === activeTextIdState ? { ...t, position: { ...t.position, ...patch } } : t,
        );
        scheduleUpdate({ ...design, texts });
      }
    },
    [design, activeLayerKind, activeTextIdState, activeEffectIdState, scheduleUpdate],
  );

  const updateActiveTiming = useCallback(
    (patch: { start_fraction?: number; end_fraction?: number }) => {
      if (activeLayerKind === "effect") {
        updateActiveEffect(patch);
      } else {
        updateActiveText(patch);
      }
    },
    [activeLayerKind, updateActiveEffect, updateActiveText],
  );

  // يعدّل توقيت أي طبقة (مو بس النشطة) - يستخدمه الشريط الزمني
  const updateLayerTiming = useCallback(
    (kind: "text" | "effect", id: string, patch: { start_fraction?: number; end_fraction?: number }) => {
      if (!design) return;
      if (kind === "effect") {
        scheduleUpdate({ ...design, effects: design.effects.map((e) => (e.id === id ? { ...e, ...patch } : e)) });
      } else {
        scheduleUpdate({ ...design, texts: design.texts.map((x) => (x.id === id ? { ...x, ...patch } : x)) });
      }
    },
    [design, scheduleUpdate],
  );

  const activeText =
    activeLayerKind === "text" ? design?.texts.find((t) => t.id === activeTextIdState) ?? design?.texts[0] ?? null : null;
  const activeEffect =
    activeLayerKind === "effect" ? design?.effects.find((e) => e.id === activeEffectIdState) ?? null : null;

  const activeTiming = activeText
    ? { start_fraction: activeText.start_fraction, end_fraction: activeText.end_fraction }
    : activeEffect
      ? { start_fraction: activeEffect.start_fraction, end_fraction: activeEffect.end_fraction }
      : null;

  const value = useMemo<DesignContextValue>(
    () => ({
      design,
      template,
      previewJson,
      isRendering,
      renderError,
      activeTextId: activeText?.id ?? null,
      activeText,
      activeEffectId: activeEffect?.id ?? null,
      activeEffect,
      activeLayerKind,
      startDesign,
      setActiveTextId,
      addText,
      addIconLayer,
      removeText,
      updateActiveText,
      setActiveEffectId,
      addEffect,
      removeEffect,
      updateActiveEffect,
      updateActivePosition,
      activeTiming,
      updateActiveTiming,
      updateLayerTiming,
      overlayClose,
      setOverlayClose,
      activePen,
      setActivePen: setActivePenExclusive,
      addDrawingStroke,
      addAiDrawingStrokes,
      removeDrawingStroke,
      undoLastStroke,
      clearDrawings,
      isErasing,
      setIsErasing: setIsErasingExclusive,
      undo,
      redo,
      canUndo: pastRef.current.length > 0,
      canRedo: futureRef.current.length > 0,
      historyEpoch,
      resumeProject,
    }),
    [
      design,
      template,
      previewJson,
      isRendering,
      renderError,
      activeText,
      activeEffect,
      activeLayerKind,
      startDesign,
      setActiveTextId,
      addText,
      addIconLayer,
      removeText,
      updateActiveText,
      setActiveEffectId,
      addEffect,
      removeEffect,
      updateActiveEffect,
      updateActivePosition,
      activeTiming,
      updateActiveTiming,
      updateLayerTiming,
      overlayClose,
      activePen,
      setActivePenExclusive,
      addDrawingStroke,
      addAiDrawingStrokes,
      removeDrawingStroke,
      undoLastStroke,
      clearDrawings,
      isErasing,
      setIsErasingExclusive,
      undo,
      redo,
      histTick,
      historyEpoch,
      resumeProject,
    ],
  );

  return <DesignContext.Provider value={value}>{children}</DesignContext.Provider>;
}

export function useDesign(): DesignContextValue {
  const ctx = useContext(DesignContext);
  if (!ctx) throw new Error("useDesign must be used within a DesignProvider");
  return ctx;
}
